import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MagnifyingGlassIcon, PlusIcon, UsersIcon, ArrowUpTrayIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getClients, createClient, previewClientImport, confirmClientImport } from '../services/clientService';
import Modal from '../components/common/Modal';
import Badge from '../components/common/Badge';
import EmptyState from '../components/common/EmptyState';
import SkeletonBlock from '../components/common/SkeletonBlock';
import ClientForm from '../components/features/clients/ClientForm';

const STATUS_FILTER_OPTIONS = [
  { value: '',         label: 'All' },
  { value: 'active',   label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'archived', label: 'Archived' },
];

const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-violet-100 text-violet-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-sky-100 text-sky-700',
];

function getAvatarColor(id) {
  // Simple hash from UUID chars
  const hash = (id || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

function ClientAvatar({ client }) {
  const initials = `${client.firstName?.[0] ?? ''}${client.lastName?.[0] ?? ''}`.toUpperCase();
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${getAvatarColor(client.id)}`}>
      {initials}
    </div>
  );
}

function TableSkeleton() {
  return (
    <div className="card-flush overflow-x-auto">
      <div className="border-b border-neutral-200 dark:border-neutral-700 px-6 py-3 bg-neutral-50 dark:bg-neutral-900/50">
        <SkeletonBlock className="h-3 w-40" rounded="rounded" />
      </div>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="flex items-center gap-4 px-6 py-4 border-b border-neutral-100 dark:border-neutral-700/50 last:border-0">
          <SkeletonBlock className="w-8 h-8 shrink-0" rounded="rounded-full" />
          <SkeletonBlock className={`h-4 ${i % 2 === 0 ? 'w-36' : 'w-28'}`} rounded="rounded" />
          <SkeletonBlock className="h-4 w-40 ml-auto hidden sm:block" rounded="rounded" />
          <SkeletonBlock className="h-5 w-14" rounded="rounded-full" />
        </div>
      ))}
    </div>
  );
}

function ImportModal({ isOpen, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [step, setStep] = useState('upload'); // upload | preview | done
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const previewMutation = useMutation({
    mutationFn: previewClientImport,
    onSuccess: (data) => { setPreview(data); setStep('preview'); },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to parse CSV'),
  });

  const confirmMutation = useMutation({
    mutationFn: confirmClientImport,
    onSuccess: (data) => {
      setResult(data);
      setStep('done');
      onImported();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Import failed'),
  });

  const handleFile = (f) => {
    setFile(f);
    previewMutation.mutate(f);
  };

  const reset = () => { setFile(null); setPreview(null); setStep('upload'); setResult(null); };
  const handleClose = () => { reset(); onClose(); };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Clients from CSV" size="xl">
      {step === 'upload' && (
        <div className="space-y-4">
          <div className="text-sm text-neutral-600 dark:text-neutral-400 space-y-1">
            <p>Upload a CSV export from SimplePractice, TherapyNotes, or any system. Sage auto-detects column names.</p>
            <p className="text-xs">Required columns: <span className="font-mono">First Name</span>, <span className="font-mono">Last Name</span>. All others are optional.</p>
          </div>
          <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ''; }} />
          <div
            className="border-2 border-dashed border-neutral-300 dark:border-neutral-600 rounded-xl p-10 text-center cursor-pointer hover:border-primary-400 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
          >
            <ArrowUpTrayIcon className="h-10 w-10 mx-auto text-neutral-400 dark:text-neutral-500 mb-3" />
            {previewMutation.isPending ? (
              <p className="text-sm text-neutral-500">Parsing…</p>
            ) : (
              <>
                <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">Drop your CSV here, or <span className="text-primary-600 hover:underline">browse</span></p>
                <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">Max 10 MB</p>
              </>
            )}
          </div>
          <div className="text-xs text-neutral-400 dark:text-neutral-500">
            <p className="font-medium mb-1">Supported column names (any order):</p>
            <p>First Name, Last Name, Email, Phone, Date of Birth, Gender, Address, City, State, Zip, Insurance, Member ID, Status, Notes, Emergency Contact…</p>
          </div>
          <div className="flex justify-end">
            <button onClick={handleClose} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {step === 'preview' && preview && (
        <div className="space-y-4">
          {/* Summary */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total rows', value: preview.totalRows },
              { label: 'Ready to import', value: preview.validRows, color: 'text-green-600 dark:text-green-400' },
              { label: 'Will skip', value: preview.invalidRows, color: preview.invalidRows > 0 ? 'text-yellow-600 dark:text-yellow-400' : '' },
            ].map(({ label, value, color }) => (
              <div key={label} className="text-center p-3 bg-neutral-50 dark:bg-neutral-800/60 rounded-lg">
                <p className={`text-2xl font-bold ${color ?? 'text-neutral-900 dark:text-neutral-100'}`}>{value}</p>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{label}</p>
              </div>
            ))}
          </div>

          {preview.unmappedHeaders.length > 0 && (
            <div className="flex items-start gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-sm text-yellow-800 dark:text-yellow-300">
              <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Unrecognized columns (will be ignored):</p>
                <p className="text-xs mt-0.5">{preview.unmappedHeaders.join(', ')}</p>
              </div>
            </div>
          )}

          {/* Row preview table */}
          <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
            <table className="w-full text-xs min-w-[500px]">
              <thead className="bg-neutral-50 dark:bg-neutral-900/50 sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 text-neutral-500 font-semibold uppercase tracking-wide">#</th>
                  <th className="text-left px-3 py-2 text-neutral-500 font-semibold uppercase tracking-wide">Name</th>
                  <th className="text-left px-3 py-2 text-neutral-500 font-semibold uppercase tracking-wide">Email</th>
                  <th className="text-left px-3 py-2 text-neutral-500 font-semibold uppercase tracking-wide">Status</th>
                  <th className="text-left px-3 py-2 text-neutral-500 font-semibold uppercase tracking-wide">Issues</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {preview.rows.map((row) => (
                  <tr key={row.rowNumber} className={row.errors.length > 0 ? 'bg-red-50 dark:bg-red-900/10' : ''}>
                    <td className="px-3 py-2 text-neutral-400">{row.rowNumber}</td>
                    <td className="px-3 py-2 font-medium text-neutral-900 dark:text-neutral-100">
                      {row.data.firstName || '—'} {row.data.lastName || ''}
                    </td>
                    <td className="px-3 py-2 text-neutral-600 dark:text-neutral-400">{row.data.email || '—'}</td>
                    <td className="px-3 py-2">
                      {row.errors.length === 0
                        ? <CheckCircleIcon className="h-4 w-4 text-green-500" />
                        : <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />}
                    </td>
                    <td className="px-3 py-2 text-red-600 dark:text-red-400">
                      {[...row.errors, ...row.warnings].join('; ') || ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.totalRows > 100 && (
            <p className="text-xs text-neutral-400">Showing first 100 rows of {preview.totalRows}. All valid rows will be imported.</p>
          )}

          <div className="flex justify-between pt-1">
            <button onClick={() => { reset(); }} className="btn btn-secondary">Back</button>
            <button
              onClick={() => confirmMutation.mutate(file)}
              disabled={confirmMutation.isPending || preview.validRows === 0}
              className="btn btn-primary disabled:opacity-60"
            >
              {confirmMutation.isPending ? 'Importing…' : `Import ${preview.validRows} client${preview.validRows !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {step === 'done' && result && (
        <div className="space-y-4 text-center py-4">
          <CheckCircleIcon className="h-12 w-12 text-green-500 mx-auto" />
          <div>
            <p className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{result.message}</p>
            {result.data?.errors?.length > 0 && (
              <div className="mt-3 text-left text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg max-h-40 overflow-y-auto divide-y divide-neutral-100 dark:divide-neutral-700">
                {result.data.errors.map((e, i) => (
                  <p key={i} className="px-3 py-2 text-neutral-600 dark:text-neutral-400">
                    <span className="font-medium">Row {e.rowNumber}:</span> {e.reason}
                  </p>
                ))}
              </div>
            )}
          </div>
          <button onClick={handleClose} className="btn btn-primary">Done</button>
        </div>
      )}
    </Modal>
  );
}

export default function Clients() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['clients', { search, status, page }],
    queryFn: () => getClients({ search: search || undefined, status: status || undefined, page, limit: 20 }),
    keepPreviousData: true,
  });

  const createMutation = useMutation({
    mutationFn: createClient,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setIsModalOpen(false);
      toast.success(data?.inviteSent ? 'Client created and portal invite sent' : 'Client created');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create client'),
  });

  // "N" key shortcut to open New Client modal
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'n' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
        e.preventDefault();
        setIsModalOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const clients = data?.clients ?? [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1>Clients</h1>
        <div className="flex gap-2">
          <button onClick={() => setIsImportOpen(true)} className="btn btn-secondary">
            <ArrowUpTrayIcon className="h-4 w-4" />
            Import CSV
          </button>
          <button onClick={() => setIsModalOpen(true)} className="btn btn-primary">
            <PlusIcon className="h-4 w-4" />
            New client
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 sm:max-w-sm">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="input pl-9"
          />
        </div>
        <div className="flex gap-1.5">
          {STATUS_FILTER_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { setStatus(opt.value); setPage(1); }}
              className={`btn btn-sm ${status === opt.value ? 'btn-primary' : 'btn-secondary'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <TableSkeleton />
      ) : isError ? (
        <div className="text-center py-20 text-danger-600 dark:text-danger-400">
          Failed to load clients. Please try again.
        </div>
      ) : clients.length === 0 ? (
        <EmptyState
          icon={UsersIcon}
          title={search || status ? 'No clients match your filters' : 'No clients yet'}
          description={search || status ? 'Try adjusting your search or filters.' : 'Add your first client to get started.'}
          action={
            !search && !status && (
              <button onClick={() => setIsModalOpen(true)} className="btn btn-primary">
                Add client
              </button>
            )
          }
        />
      ) : (
        <>
          <div className="card-flush overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
                <tr>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Name</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide hidden sm:table-cell">Email</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide hidden md:table-cell">Phone</th>
                  <th className="text-left px-6 py-3 text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700/50">
                {clients.map((client) => (
                  <tr
                    key={client.id}
                    onClick={() => navigate(`/clients/${client.id}`)}
                    className="hover:bg-neutral-50 dark:hover:bg-neutral-700/40 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-3.5">
                      <div className="flex items-center gap-3">
                        <ClientAvatar client={client} />
                        <span className="font-medium text-neutral-900 dark:text-neutral-100">
                          {client.lastName}, {client.firstName}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-3.5 text-neutral-500 dark:text-neutral-400 hidden sm:table-cell">
                      {client.email || '—'}
                    </td>
                    <td className="px-6 py-3.5 text-neutral-500 dark:text-neutral-400 hidden md:table-cell">
                      {client.phone || '—'}
                    </td>
                    <td className="px-6 py-3.5">
                      <Badge label={client.status} variant={client.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between text-sm text-neutral-600 dark:text-neutral-400">
              <span>
                Showing {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 1}
                  className="btn btn-secondary btn-sm disabled:opacity-40"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page === pagination.pages}
                  className="btn btn-secondary btn-sm disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Client Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="New Client" size="lg">
        <ClientForm
          onSubmit={(data) => createMutation.mutate(data)}
          onCancel={() => setIsModalOpen(false)}
          isSubmitting={createMutation.isPending}
        />
      </Modal>

      <ImportModal
        isOpen={isImportOpen}
        onClose={() => setIsImportOpen(false)}
        onImported={() => queryClient.invalidateQueries({ queryKey: ['clients'] })}
      />
    </div>
  );
}
